import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Search, Shield, Download, AlertTriangle, Calendar, Grid, List, FolderKanban } from 'lucide-react';
import SharedUsersPopover from '@/components/SharedUsersPopover';
import * as XLSX from 'xlsx';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const calculateExpiryDays = (expiryDate) => {
  if (!expiryDate) return null;
  const today = new Date();
  const expiry = new Date(expiryDate);
  const diffTime = expiry - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

const getExpiryStatus = (days) => {
  if (days === null) return null;
  if (days < 0) return { label: 'Expired', color: 'text-red-600', bg: 'bg-red-100' };
  if (days <= 7) return { label: `${days} day${days !== 1 ? 's' : ''}`, color: 'text-orange-600', bg: 'bg-orange-100' };
  if (days <= 30) return { label: `${days} days`, color: 'text-yellow-600', bg: 'bg-yellow-100' };
  return { label: `${days} days`, color: 'text-green-600', bg: 'bg-green-100' };
};

export default function CertificatesList({ user }) {
  const [certificates, setCertificates] = useState([]);
  const [filteredCertificates, setFilteredCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState('All');
  const [projects, setProjects] = useState([]);
  const [viewMode, setViewMode] = useState('card');
  const navigate = useNavigate();

  const fetchCertificates = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/certificates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCertificates(response.data);
    } catch (error) {
      toast.error('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/projects`, { headers: { Authorization: `Bearer ${token}` } });
      setProjects(res.data);
    } catch (err) {
      // Non-critical
    }
  }, []);

  useEffect(() => {
    fetchCertificates();
    fetchProjects();
  }, [fetchCertificates, fetchProjects]);

  // Filter certificates when dependencies change
  useEffect(() => {
    let filtered = certificates;

    if (searchQuery) {
      filtered = filtered.filter(
        (cert) =>
          cert.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          cert.certificate_type.toLowerCase().includes(searchQuery.toLowerCase()) ||
          cert.certificate_provider.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (cert.domain_names && cert.domain_names.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    if (selectedProject !== 'All') {
      filtered = filtered.filter((cert) => cert.project_id === selectedProject);
    }

    setFilteredCertificates(filtered);
  }, [certificates, searchQuery, selectedProject]);

  const exportToExcel = () => {
    const exportData = certificates.map(cert => ({
      Title: cert.title,
      Type: cert.certificate_type,
      Provider: cert.certificate_provider,
      Format: cert.certificate_format,
      'Expiry Date': new Date(cert.expiry_date).toLocaleDateString(),
      Domains: cert.domain_names,
      'Created At': new Date(cert.created_at).toLocaleDateString(),
      Shared: cert.shared_with.length > 0 ? 'Yes' : 'No'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Certificates');
    XLSX.writeFile(wb, `keebox-certificates-${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Exported to Excel successfully');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-text-primary font-manrope text-xl">Loading certificates...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="font-manrope font-bold text-4xl text-text-primary tracking-tight">
            Certificates
          </h1>
          <p className="text-text-secondary mt-2">
            {certificates.length} certificate{certificates.length !== 1 ? 's' : ''} managed
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={exportToExcel}
            className="btn-outline flex items-center gap-2"
            data-testid="btn-export-certificates"
          >
            <Download size={20} />
            Export
          </button>
          {user?.role !== 'viewer' && (
            <button
              onClick={() => navigate('/dashboard/certificates/add')}
              className="btn-primary flex items-center gap-2"
              data-testid="btn-add-certificate"
            >
              <Plus size={20} />
              Add Certificate
            </button>
          )}
        </div>
      </div>

      <div className="mb-6 flex gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" size={20} />
          <input
            type="text"
            placeholder="Search certificates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-white border-2 border-gray-200 rounded-lg pl-10 pr-4 py-3 focus:border-primary focus:outline-none text-text-primary"
            data-testid="input-search-certificates"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('card')}
            className={`p-3 rounded-lg transition-colors ${
              viewMode === 'card'
                ? 'bg-primary text-white'
                : 'bg-white border-2 border-gray-200 text-text-primary hover:border-primary'
            }`}
          >
            <Grid size={20} />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-3 rounded-lg transition-colors ${
              viewMode === 'list'
                ? 'bg-primary text-white'
                : 'bg-white border-2 border-gray-200 text-text-primary hover:border-primary'
            }`}
          >
            <List size={20} />
          </button>
        </div>
      </div>

      {/* Project filter */}
      <div className="mb-6 flex items-center gap-3">
        <FolderKanban size={16} className="text-text-muted" />
        <select
          value={selectedProject}
          onChange={(e) => setSelectedProject(e.target.value)}
          className="bg-white border-2 border-gray-200 rounded-lg px-4 py-2 focus:border-primary focus:outline-none text-text-primary text-sm"
          data-testid="filter-cert-project"
        >
          <option value="All">All Projects</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      {filteredCertificates.length === 0 ? (
        <div className="text-center py-20">
          <Shield className="w-16 h-16 text-text-muted mx-auto mb-4" strokeWidth={1.5} />
          <h3 className="font-manrope font-semibold text-xl text-text-primary mb-2">
            No certificates found
          </h3>
          <p className="text-text-secondary mb-6">
            {searchQuery ? 'Try adjusting your search' : 'Get started by adding your first certificate'}
          </p>
          {user?.role !== 'viewer' && !searchQuery && (
            <button
              onClick={() => navigate('/dashboard/certificates/add')}
              className="btn-primary"
            >
              Add First Certificate
            </button>
          )}
        </div>
      ) : (
        <>
          {viewMode === 'card' ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCertificates.map((cert) => {
                const days = calculateExpiryDays(cert.expiry_date);
                const status = getExpiryStatus(days);
                
                return (
                  <div
                    key={cert.id}
                    onClick={() => navigate(`/dashboard/certificates/${cert.id}`)}
                    className="bg-white border-2 border-gray-200 rounded-xl p-6 cursor-pointer credential-card hover:shadow-lg"
                    data-testid={`certificate-card-${cert.id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="bg-primary/10 p-3 rounded-lg">
                        <Shield className="w-6 h-6 text-primary" strokeWidth={1.5} />
                      </div>
                      {status && (
                        <div className={`flex items-center gap-1 px-3 py-1 rounded-full ${status.bg}`}>
                          {days < 0 ? (
                            <AlertTriangle className={`w-4 h-4 ${status.color}`} />
                          ) : (
                            <Calendar className={`w-4 h-4 ${status.color}`} />
                          )}
                          <span className={`text-xs font-medium ${status.color}`}>
                            {status.label}
                          </span>
                        </div>
                      )}
                    </div>
                    <h3 className="font-manrope font-semibold text-xl text-text-primary mb-2">
                      {cert.title}
                    </h3>
                    <p className="text-text-secondary text-sm mb-1">{cert.certificate_type}</p>
                    <p className="text-text-muted text-xs mb-3">{cert.certificate_provider}</p>
                    {cert.domain_names && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {cert.domain_names.split(',').slice(0, 2).map((domain, i) => (
                          <span key={i} className="text-xs px-2 py-1 bg-blue-50 text-blue-600 rounded-full">
                            {domain.trim()}
                          </span>
                        ))}
                        {cert.domain_names.split(',').length > 2 && (
                          <span className="text-xs px-2 py-1 bg-gray-100 text-text-muted rounded-full">
                            +{cert.domain_names.split(',').length - 2} more
                          </span>
                        )}
                      </div>
                    )}
                    <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                      <p className="text-xs text-text-muted">
                        Expires: {new Date(cert.expiry_date).toLocaleDateString()}
                      </p>
                      <SharedUsersPopover sharedWith={cert.shared_with} resourceId={cert.id} resourceType="certificate" onUnshare={fetchCertificates} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Title</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Type</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Provider</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Expiry</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-text-primary">Shared</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredCertificates.map((cert) => {
                    const days = calculateExpiryDays(cert.expiry_date);
                    const status = getExpiryStatus(days);
                    
                    return (
                      <tr
                        key={cert.id}
                        onClick={() => navigate(`/dashboard/certificates/${cert.id}`)}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="bg-primary/10 p-2 rounded-lg">
                              <Shield className="w-4 h-4 text-primary" />
                            </div>
                            <span className="font-medium text-text-primary">{cert.title}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-text-secondary text-sm">{cert.certificate_type}</td>
                        <td className="px-6 py-4 text-text-secondary text-sm">{cert.certificate_provider}</td>
                        <td className="px-6 py-4 text-text-secondary text-sm">
                          {new Date(cert.expiry_date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          {status && (
                            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full ${status.bg} ${status.color} text-xs font-medium`}>
                              {days < 0 ? <AlertTriangle size={12} /> : <Calendar size={12} />}
                              {status.label}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <SharedUsersPopover sharedWith={cert.shared_with} resourceId={cert.id} resourceType="certificate" onUnshare={fetchCertificates} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}